import React, { createRef, useMemo } from 'react';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import cx from 'classnames';
import { Pagination, Dimmer, Loader } from 'semantic-ui-react';
import Slugger from 'github-slugger';
import { Icon } from '@plone/volto/components';
import { renderLinkElement } from '@plone/volto-slate/editor/render';
import config from '@plone/volto/registry';
import withQuerystringResults from '@plone/volto/components/manage/Blocks/Listing/withQuerystringResults';
import { normalizeString } from '@plone/volto/helpers';
import paginationLeftSVG from '@plone/volto/icons/left-key.svg';
import paginationRightSVG from '@plone/volto/icons/right-key.svg';

const messages = defineMessages({
  previousPage: { id: 'previous_page', defaultMessage: 'Página anterior' },
  nextPage: { id: 'next_page', defaultMessage: 'Próxima página' },
  resultsRange: {
    id: 'results_range',
    defaultMessage: 'Resultados {start}\u2013{end} de {total}',
  },
});

const Headline = ({ headlineTag, id, data = {}, listingItems, isEditMode }) => {
  let attr = { id };
  const slug = Slugger.slug(normalizeString(data.headline));
  attr.id = slug || id;
  const LinkedHeadline = useMemo(
    () => renderLinkElement(headlineTag),
    [headlineTag],
  );
  return (
    <LinkedHeadline
      mode={!isEditMode && 'view'}
      children={data.headline}
      attributes={attr}
      className={cx('headline', { emptyListing: !listingItems?.length > 0 })}
    />
  );
};

const ListingBody = withQuerystringResults((props) => {
  const {
    data = {},
    isEditMode,
    listingItems,
    totalPages,
    onPaginationChange,
    variation,
    currentPage,
    prevBatch,
    nextBatch,
    isFolderContentsListing,
    hasLoaded,
    id,
  } = props;

  const variations = config.blocks?.blocksConfig['listing']?.variations || [];
  const defaultVariation = variations.filter((item) => item.isDefault)?.[0];
  let ListingBodyTemplate;
  if (data.template && !data.variation) {
    const legacyTemplateConfig = variations.find(
      (item) => item.id === data.template,
    );
    ListingBodyTemplate = legacyTemplateConfig?.template;
  } else {
    ListingBodyTemplate =
      variation?.template ?? defaultVariation?.template ?? null;
  }

  const listingRef = createRef();
  const NoResults = variation?.noResultsComponent
    ? variation.noResultsComponent
    : config.blocks?.blocksConfig['listing'].noResultsComponent;
  const HeadlineTag = data.headlineTag || 'h2';

  const pageSize =
    data.batch_size ||
    variation?.batch_size ||
    config.blocks?.blocksConfig['listing']?.defaultPageSize ||
    25;
  const totalItems = listingItems?.length
    ? data.show_total
      ? data.total || totalPages * pageSize
      : data.total || totalPages * pageSize
    : data.total || 0;
  const startIndex = listingItems?.length
    ? (currentPage - 1) * pageSize + 1
    : 0;
  const endIndex = listingItems?.length
    ? startIndex + listingItems.length - 1
    : 0;

  return (
    <>
      {data.headline && (
        <Headline
          headlineTag={HeadlineTag}
          id={id}
          listingItems={listingItems}
          data={data}
          isEditMode={isEditMode}
        />
      )}
      {listingItems?.length > 0 ? (
        <div ref={listingRef}>
          <ListingBodyTemplate
            items={listingItems}
            isEditMode={isEditMode}
            {...data}
            {...variation}
          />
          {totalPages > 1 && (
            <div
              className="pagination-wrapper"
              data-override="listingbody-manage"
            >
              <Pagination
                className="translated-pagination"
                activePage={currentPage}
                totalPages={totalPages}
                onPageChange={(e, { activePage }) => {
                  !isEditMode &&
                    listingRef.current.scrollIntoView({ behavior: 'smooth' });
                  onPaginationChange(e, { activePage });
                }}
                firstItem={null}
                lastItem={null}
                prevItem={{
                  content: (
                    <span aria-hidden="true" className="pagination-label prev">
                      <Icon name={paginationLeftSVG} size="18px" />
                      <span className="text">
                        <FormattedMessage {...messages.previousPage} />
                      </span>
                    </span>
                  ),
                  'aria-label': props.intl.formatMessage(messages.previousPage),
                  icon: true,
                  'aria-disabled': !prevBatch,
                  className: !prevBatch ? 'disabled' : null,
                }}
                nextItem={{
                  content: (
                    <span aria-hidden="true" className="pagination-label next">
                      <span className="text">
                        <FormattedMessage {...messages.nextPage} />
                      </span>
                      <Icon name={paginationRightSVG} size="18px" />
                    </span>
                  ),
                  'aria-label': props.intl.formatMessage(messages.nextPage),
                  icon: true,
                  'aria-disabled': !nextBatch,
                  className: !nextBatch ? 'disabled' : null,
                }}
              />
            </div>
          )}
          {totalItems > 0 && (
            <div className="results-range after-pagination" aria-live="polite">
              <FormattedMessage
                {...messages.resultsRange}
                values={{ start: startIndex, end: endIndex, total: totalItems }}
              />
            </div>
          )}
        </div>
      ) : isEditMode ? (
        <div className="listing message" ref={listingRef}>
          {isFolderContentsListing && (
            <FormattedMessage
              id="No items found in this container."
              defaultMessage="No items found in this container."
            />
          )}
          {hasLoaded && NoResults && (
            <NoResults isEditMode={isEditMode} {...data} />
          )}
          <Dimmer active={!hasLoaded} inverted>
            <Loader indeterminate size="small">
              <FormattedMessage id="loading" defaultMessage="Loading" />
            </Loader>
          </Dimmer>
        </div>
      ) : (
        <div className="emptyListing">
          {hasLoaded && NoResults && (
            <NoResults isEditMode={isEditMode} {...data} />
          )}
          <Dimmer active={!hasLoaded} inverted>
            <Loader indeterminate size="small">
              <FormattedMessage id="loading" defaultMessage="Loading" />
            </Loader>
          </Dimmer>
        </div>
      )}
    </>
  );
});

export default injectIntl(ListingBody);
